import { useState } from 'react';
import { ZymplerRow } from '@/types/businesscase';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';

interface ZymplerPreviewTableProps {
  data: ZymplerRow[];
  onUpdateRow: (yearIndex: number, field: string, value: number) => void;
}

export function ZymplerPreviewTable({ data, onUpdateRow }: ZymplerPreviewTableProps) {
  const [editingCell, setEditingCell] = useState<{ row: number; field: string } | null>(null);

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('nl-NL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const columns = [
    { key: 'grid_connection_savings', label: 'Grid Connection' },
    { key: 'grid_purchase_cost_savings', label: 'Grid Purchase' },
    { key: 'dayahead_incomes', label: 'Dayahead' },
    { key: 'hbe_incomes', label: "HBE's" },
    { key: 'energy_tax_savings', label: 'Energy Tax' },
    { key: 'endex_cost_savings', label: 'Endex' },
    { key: 'imbalance_income', label: 'Imbalance Income' },
    { key: 'imbalance_discount', label: 'Imbalance Discount' },
    { key: 'ems_costs', label: 'EMS Costs' },
    { key: 'total_opex', label: 'Total OPEX' },
    { key: 'generator_cost_savings', label: 'Generator' },
    { key: 'extra_sde', label: 'Extra SDE' },
    { key: 'total_solar_revenues', label: 'Total Solar' },
  ];

  const handleCellEdit = (rowIndex: number, field: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    onUpdateRow(rowIndex, field, numValue);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Preview Geïmporteerde Data (bewerken mogelijk)</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="min-w-full border-collapse text-xs">
              <thead className="bg-muted sticky top-0 z-10">
                <tr>
                  <th className="p-2 text-left font-semibold sticky left-0 bg-muted z-20">Jaar</th>
                  {columns.map(col => (
                    <th key={col.key} className="p-2 text-left font-semibold whitespace-nowrap">
                      {col.label}
                    </th>
                  ))}
                  <th className="p-2 text-left font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, rowIndex) => (
                  <tr 
                    key={row.year} 
                    className="border-b border-border hover:bg-muted/30"
                  >
                    <td className="p-2 font-bold sticky left-0 bg-background z-10">{row.year}</td>
                    {columns.map(col => {
                      const value = Number(row.mapped[col.key as keyof typeof row.mapped] || 0);
                      const isEditing = 
                        editingCell?.row === rowIndex && 
                        editingCell?.field === col.key;
                      
                      return (
                        <td key={col.key} className="p-1">
                          {isEditing ? (
                            <Input
                              type="number"
                              step="0.01"
                              value={value}
                              onChange={(e) => handleCellEdit(rowIndex, col.key, e.target.value)}
                              onFocus={() => setEditingCell({ row: rowIndex, field: col.key })}
                              onBlur={() => setEditingCell(null)}
                              className="h-7 text-xs ring-2 ring-primary"
                            />
                          ) : (
                            <div 
                              className="h-7 flex items-center px-2 text-xs cursor-pointer hover:bg-muted/50 rounded"
                              onClick={() => setEditingCell({ row: rowIndex, field: col.key })}
                            >
                              {formatNumber(value)}
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td className="p-2">
                      {row.warnings && row.warnings.length > 0 ? (
                        <div className="flex items-center gap-1 text-orange-600">
                          <AlertCircle className="w-3 h-3" />
                          <span className="text-xs">{row.warnings.length}</span>
                        </div>
                      ) : (
                        <span className="text-green-600 text-xs">✓</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Klik op een cel om de waarde aan te passen vóór import
        </p>
      </CardContent>
    </Card>
  );
}
